import type {MouseEvent, ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Icon} from '@Components/icon-v2';
import {InputField} from '@Components/input-field-v2';
import {Text, TextSize} from '@Components/text';
import {InputFieldType} from '@Components/input-field-v2/input-field.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Dropdown} from '@Components/dropdown-v2';
import {noop} from '@Utils/general.util';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {addClickOutsideListener} from '@Utils/dom.util';
import {convert12HrTo24Hr, convert24To12Hr, createTimeSlots} from './time-input.helper';
import type {TimeInputProps} from './time-input.types';
import {DayNightSelector} from './time-input.types';
import styles from './time-input.module.scss';

export function TimeInput({
  onSelectTime,
  dropdownID,
  shouldDisplayIcon = true,
  defaultSelectedDateTime = new Date(),
  onTimeInputFocusCallback = noop,
  onTimeInputBlurCallback = noop,
  isInErrorState = false,
  onClickSelector = undefined,
}: TimeInputProps): ReactElement<any> {
  const newTimeslots = createTimeSlots();
  const [customTimeHour, setCustomTimeHour] = useState<number>(convert24To12Hr(defaultSelectedDateTime.getHours()));
  const [customTimeMinute, setCustomTimeMinute] = useState<number>(defaultSelectedDateTime.getMinutes());
  const [dayNightSelector, setDayNightSelector] = useState<DayNightSelector>(defaultSelectedDateTime.getHours() < 12 ? DayNightSelector.AM : DayNightSelector.PM);
  const [timeSlots] = useState<string[]>(newTimeslots);
  const inputRef = useRef<HTMLInputElement>(null);
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setCustomTimeHour(convert24To12Hr(defaultSelectedDateTime.getHours()));
    setCustomTimeMinute(defaultSelectedDateTime.getMinutes());
    setDayNightSelector(defaultSelectedDateTime.getHours() < 12 ? DayNightSelector.AM : DayNightSelector.PM);
  }, [defaultSelectedDateTime.getHours(), defaultSelectedDateTime.getMinutes()]);

  const onTimeUpdated = (hr: number, min: number, dayNight: DayNightSelector, isAmPmToggle: boolean = false, isFromDropdown: boolean = false): void => {
    if (onSelectTime) {
      onSelectTime(convert12HrTo24Hr(dayNight, hr), min, isAmPmToggle, isFromDropdown);
    }
  };

  const onHourUpdated = (hr: number): void => {
    setCustomTimeHour(hr);
    onTimeUpdated(hr, customTimeMinute, dayNightSelector);
  };

  const onMinuteUpdated = (min: number): void => {
    setCustomTimeMinute(min);
    onTimeUpdated(customTimeHour, min, dayNightSelector);
  };

  const onDayNightUpdated = (dayNight: DayNightSelector): void => {
    setDayNightSelector(dayNight);
    onTimeUpdated(customTimeHour, customTimeMinute, dayNight, true);
  };

  const updateCustomTimeFromDropdown = (time: string): void => {
    const splitArr = time.split(':');
    const hour = Number(splitArr[0]);
    const [minute, dayTime] = splitArr[1].split(' ');
    const dayNight = DayNightSelector[dayTime.toUpperCase() as keyof typeof DayNightSelector];

    setCustomTimeHour(hour);
    setCustomTimeMinute(Number(minute));
    setDayNightSelector(dayNight);
    onTimeUpdated(hour, Number(minute), dayNight, true);
  };

  const updateCustomTimeHour = (hour: number | string): void => {
    let inputHr = hour;

    if (typeof hour === 'string') {
      inputHr = Number(hour);
    }

    if (typeof inputHr === 'number') {
      if (inputHr < 0) {
        inputHr = 0;
      }

      if (inputHr >= 12) {
        inputHr = 12;
      }
      onHourUpdated(inputHr);
    }
  };

  const updateCustomTimeMinute = (minute: number | string): void => {
    let inputMin = minute;

    if (typeof minute === 'string') {
      inputMin = Number(minute);
    }

    if (typeof inputMin === 'number') {
      if (inputMin < 0) {
        inputMin = 0;
      }

      if (inputMin >= 60) {
        inputMin = 59;
      }
      onMinuteUpdated(inputMin);
    }
  };

  const toggleDayNightSelector = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    if (dayNightSelector === DayNightSelector.AM) {
      onDayNightUpdated(DayNightSelector.PM);
      return;
    }
    onDayNightUpdated(DayNightSelector.AM);
  };

  const formatTimeForSelector = (time: number): string => {
    if (time < 10) {
      return `0${time}`;
    }
    return time.toString();
  };

  const onTimeInputFocus = (): void => {
    onTimeInputFocusCallback();
  };

  const onTimeInputBlur = (): void => {
    if (onTimeInputBlurCallback) {
      onTimeInputBlurCallback(convert12HrTo24Hr(dayNightSelector, customTimeHour), customTimeMinute);
    }
  };

  const onDropdownSelectorClick = (e: MouseEvent): void => {
    e.stopPropagation();

    if (onClickSelector) {
      onClickSelector();
    } else {
      setOpenDropdown(true);
    }
  };

  const onListItemClick = (timeslot: string): void => {
    inputRef.current?.focus();
    updateCustomTimeFromDropdown(timeslot);
    setOpenDropdown(false);
  };

  const getSelector = (): ReactElement<any> => {
    const getAmPmToggle = (): ReactElement<any> => {
      return (
        <span className={`${styles.amPmToggle} content-body`}>
          <ClickableDiv onClick={toggleDayNightSelector}>
            <Text val={dayNightSelector.toString()} size={TextSize.XSMALL} />
          </ClickableDiv>
        </span>
      );
    };

    const getBorderClass = (): string => {
      if (isInErrorState) {
        return 'border-s-danger';
      }
      return 'border-s-dark';
    };

    return (
      <div className="flexbox radius-8" key={dropdownID}>
        <ClickableDiv
          key={dropdownID}
          className={`${styles.inputContainer} ${getBorderClass()} border-s-dark radius-8 flex-row-align-center justify-content-center`}
          onFocus={onTimeInputFocus}
          onBlur={onTimeInputBlur}
          onClick={onDropdownSelectorClick}
        >
          {shouldDisplayIcon ? <Icon className={`${styles.customSpacingMR6} content-disabled`} icon="icon-clock-o" size={IconSize.SIZE_ICON_16} /> : undefined}
          <div className="flex-row-align-center">
            <InputField
              inputClassName="_borderless _unpadded"
              placeholder="--"
              className={styles.inputFieldContainer}
              input={formatTimeForSelector(customTimeHour)}
              inputType={InputFieldType.NUMBER}
              maxLength={2}
              onInputChange={(val) => {
                updateCustomTimeHour(val.valueOf());
              }}
              onArrowDownKeyDown={() => {
                updateCustomTimeHour(customTimeHour - 1);
              }}
              onArrowUpKeyDown={() => {
                updateCustomTimeHour(customTimeHour + 1);
              }}
              ref={inputRef}
            />
            <Text val=":" size={TextSize.XXSMALL} bold />
            <InputField
              inputClassName="_borderless _unpadded"
              className={`${styles.inputFieldContainer} spacing-m-l-1`}
              placeholder="--"
              input={formatTimeForSelector(customTimeMinute)}
              inputType={InputFieldType.NUMBER}
              maxLength={2}
              onInputChange={(val) => {
                updateCustomTimeMinute(val.valueOf());
              }}
              onArrowDownKeyDown={() => {
                updateCustomTimeMinute(customTimeMinute - 1);
              }}
              onArrowUpKeyDown={() => {
                updateCustomTimeMinute(customTimeMinute + 1);
              }}
            />
            {getAmPmToggle()}
          </div>
        </ClickableDiv>
      </div>
    );
  };

  const isDropdownItemSelected = (timeslot: string): boolean => {
    return timeslot === `${formatTimeForSelector(customTimeHour)}:${formatTimeForSelector(customTimeMinute)} ${dayNightSelector.toString()}`;
  };

  const getContent = (): ReactElement<any> => {
    return (
      <ControlledList
        scrollIntoViewIfSelected
        id={dropdownID}
        items={timeSlots.map((timeslot) => {
          return {
            id: `${dropdownID}_${timeslot}`,
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
            text: timeslot,
            className: styles.listItem,
            isSelected: isDropdownItemSelected(timeslot),
            onClick(): void {
              onListItemClick(timeslot);
            },
          };
        })}
      />
    );
  };

  const onCloseDropdown = (): void => {
    if (openDropdown) {
      setOpenDropdown(false);
    }
  };

  useEffect(() => {
    addClickOutsideListener(`#${dropdownID}`, () => {
      setOpenDropdown(false);
    });
  }, [dropdownContainerRef.current]);

  return (
    <div key={dropdownID} id={dropdownID} ref={dropdownContainerRef}>
      <Dropdown
        id={dropdownID}
        doCloseOnInnerClick={false}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        popUpHasCustomWidth
        popupSpacingClass="spacing-p-t-3 spacing-p-l-3 spacing-p-r-3"
        selector={getSelector()}
        popup={getContent()}
        popupClassName={`${styles.popup} border-s-standard radius-8`}
        onClose={onCloseDropdown}
        open={openDropdown}
        position={DROPDOWN_POSITION.FIXED}
      />
    </div>
  );
}
